import { useState, useEffect, useRef } from "react"
import { CheckCircle, Clock, Link2, BarChart2, Download } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const ALL_SIGNERS = [
  { name: "Sarah Johnson",   initSigned: true  },
  { name: "Mike Chen",       initSigned: true  },
  { name: "Emma Davis",      initSigned: true  },
  { name: "Alex Rodriguez",  initSigned: false },
  { name: "Lisa Park",       initSigned: false },
  { name: "James Wilson",    initSigned: false },
]

const TOTAL = 25
const INIT_SIGNED = 12

function useCountUp(target: number, duration = 900, delay = 400) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    const timeout = setTimeout(() => {
      const start = performance.now()
      const tick = (now: number) => {
        const elapsed = now - start
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        setValue(Math.round(eased * target))
        if (progress < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    }, delay)
    return () => clearTimeout(timeout)
  }, [target, duration, delay])
  return value
}

export default function HeroSection() {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'he'

  const [progress, setProgress] = useState(0)
  useEffect(() => {
    const timer = setTimeout(() => setProgress(48), 400)
    return () => clearTimeout(timer)
  }, [])

  const pct = useCountUp(48, 900, 400)
  const signedCount = useCountUp(INIT_SIGNED, 900, 400)

  const [statuses, setStatuses] = useState<boolean[]>(
    ALL_SIGNERS.map((s) => s.initSigned)
  )
  const [justSigned, setJustSigned] = useState<boolean[]>(ALL_SIGNERS.map(() => false))
  const [visible, setVisible] = useState<boolean[]>(
    ALL_SIGNERS.map((_, i) => i < 5)
  )

  const cycleRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingIndexRef = useRef(
    ALL_SIGNERS.findIndex((s) => !s.initSigned)
  )

  useEffect(() => {
    const schedule = () => {
      cycleRef.current = setTimeout(() => {
        const idx = pendingIndexRef.current
        if (idx === -1 || idx >= ALL_SIGNERS.length) return

        setVisible((prev) => {
          const next = [...prev]
          next[idx] = true
          return next
        })

        setTimeout(() => {
          setStatuses((prev) => {
            const next = [...prev]
            next[idx] = true
            return next
          })
          setJustSigned((prev) => {
            const next = [...prev]
            next[idx] = true
            return next
          })
          setTimeout(() => {
            setJustSigned((prev) => {
              const next = [...prev]
              next[idx] = false
              return next
            })
          }, 500)

          let next = idx + 1
          while (next < ALL_SIGNERS.length && ALL_SIGNERS[next].initSigned) next++
          if (next >= ALL_SIGNERS.length) {
            setTimeout(() => {
              setStatuses(ALL_SIGNERS.map((s) => s.initSigned))
              setVisible(ALL_SIGNERS.map((_, i) => i < 5))
              pendingIndexRef.current = ALL_SIGNERS.findIndex((s) => !s.initSigned)
              schedule()
            }, 2500)
            return
          }
          pendingIndexRef.current = next
          schedule()
        }, 600)
      }, 3500)
    }

    const init = setTimeout(schedule, 2000)
    return () => {
      clearTimeout(init)
      if (cycleRef.current) clearTimeout(cycleRef.current)
    }
  }, [])

  return (
    <section
      className="pt-14"
      style={{ background: "var(--hero-bg)" }}
    >
      <style>{`
        @keyframes pop-in {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.25); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes slide-up {
          from { transform: translateY(16px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes badge-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
          50%       { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
        }
        @keyframes status-fade {
          from { opacity: 0; transform: translateX(6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .signer-pop { animation: pop-in 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .signer-slide { animation: slide-up 0.4s ease-out forwards; }
        .badge-pulse { animation: badge-pulse 2s ease-in-out infinite; }
        .status-fade { animation: status-fade 0.3s ease-out forwards; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isRTL ? "lg:flex-row-reverse" : ""}`}>

          <div className="space-y-6">
            <h1
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white leading-tight"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('landing.hero.headline')}
            </h1>

            <p
              className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t('landing.hero.subheadline')}
            </p>

            <div
              className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <span className="text-green-500">{'\u2713'}</span>
              <span>{t('landing.hero.trustLine1')}</span>
              <span className="text-gray-400 mx-1">{'\u2022'}</span>
              <span className="font-bold">{t('landing.hero.trustLine2')}</span>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to={`/${i18n.language}/create`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors text-base"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.hero.ctaPrimary')}
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 text-gray-700 dark:text-gray-200 font-semibold rounded-lg transition-colors text-base bg-white dark:bg-gray-800"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('landing.hero.ctaSecondary')}
              </a>
            </div>

            <div className="flex flex-wrap gap-8 pt-2">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <Link2 size={16} className="text-green-500 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-700 dark:text-gray-200">{t('landing.hero.featureLink')}</div>
                  <div className="text-xs">{t('landing.hero.featureLinkSub')}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <BarChart2 size={16} className="text-green-500 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-700 dark:text-gray-200">{t('landing.hero.featureStatus')}</div>
                  <div className="text-xs">{t('landing.hero.featureStatusSub')}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <Download size={16} className="text-green-500 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-700 dark:text-gray-200">{t('landing.hero.featureExport')}</div>
                  <div className="text-xs">{t('landing.hero.featureExportSub')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative mt-8 lg:mt-0">
            <div
              className={`badge-pulse absolute -top-5 z-10 bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-xl shadow-lg whitespace-pre-line text-center leading-tight ${isRTL ? 'left-4 -rotate-2' : 'right-4 rotate-2'}`}
            >
              {t('landing.hero.cardBadge')}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 relative overflow-hidden">

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      {t('landing.hero.cardTitle')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {signedCount} of {TOTAL} signed
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums"
                    style={{ fontFamily: "'DM Sans', sans-serif", minWidth: "3.5ch" }}
                  >
                    {pct}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('landing.hero.cardComplete')}</div>
                </div>
              </div>

              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5 mb-5">
                <div
                  className="bg-green-500 h-2.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="space-y-3">
                {ALL_SIGNERS.map((signer, i) => {
                  if (!visible[i]) return null
                  const signed = statuses[i]
                  const popping = justSigned[i]
                  const isNew = !signer.initSigned

                  return (
                    <div
                      key={signer.name}
                      className={`flex items-center justify-between ${isNew ? "signer-slide" : ""}`}
                      style={isNew ? { animationDelay: "0ms" } : undefined}
                    >
                      <div className="flex items-center gap-3">
                        {signed ? (
                          <div
                            className={`w-8 h-8 bg-green-500 rounded-full flex items-center justify-center ${popping ? "signer-pop" : ""}`}
                          >
                            <CheckCircle size={16} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <Clock size={14} className="text-gray-400 dark:text-gray-300" />
                          </div>
                        )}
                        <span
                          className="text-sm font-medium text-gray-700 dark:text-gray-200"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {signer.name}
                        </span>
                      </div>
                      <span
                        key={`${signer.name}-${signed}`}
                        className={`text-xs font-semibold status-fade ${signed ? "text-green-600 dark:text-green-400" : "text-gray-400 dark:text-gray-500"}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {signed ? t('landing.hero.signerSigned') : t('landing.hero.signerPending')}
                      </span>
                    </div>
                  )
                })}
              </div>

              <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Live updates
                </span>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
